// Customer order history — the feed of completed orders (confirmed, cancelled,
// refunded, disputed), with the official receipt downloadable for confirmed
// deliveries. In-flight orders live on the Requests page.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';
import { requests, receipts, type RequestStatus, type ServiceRequestWithItems, type CertLang } from '../../lib/api';
import { formatHtgMinor, formatUsdFromHtgMinor } from '../../lib/money';

const TERMINAL: RequestStatus[] = ['confirmed', 'cancelled', 'refunded', 'disputed'];


export function CustomerHistoryPage() {
  const q = useQuery({ queryKey: ['me', 'requests'], queryFn: () => requests.list() });

  const rows = useMemo(() =>
    (q.data ?? [])
      .filter((r) => TERMINAL.includes(r.status))
      .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    [q.data]);

  const confirmed = useMemo(() => rows.filter((r) => r.status === 'confirmed'), [rows]);
  const spent = useMemo(() => confirmed.reduce((a, r) => a + (r.cart_subtotal_htg_minor || 0), 0), [confirmed]);

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useCustomerNav(CUSTOMER_NAV);
  const [filter, setFilter] = useState('all');

  // Product filters keyed on the real category slug. The design offers
  // Groceries / Airtime / Money; airtime and money orders do not live in this
  // data source at all, so the chips are built from categories that actually
  // appear rather than promising tabs that can never match a row.
  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      if (r.category?.slug) seen.set(r.category.slug, r.category.label);
    }
    return [...seen].map(([key, label]) => ({ key, label }));
  }, [rows]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.category?.slug === filter)),
    [rows, filter]);

  const columns: Column<ServiceRequestWithItems>[] = [
    {
      key: 'date', header: 'Date', type: 'muted', width: '120px',
      value: (r) => fmtDate(r.updated_at),
    },
    {
      key: 'ref', header: 'Reference', type: 'mono', width: '110px',
      value: (r) => `REQ-${String(r.id).padStart(4, '0')}`,
    },
    {
      key: 'product', header: 'Product', type: 'text', width: '130px',
      value: (r) => r.category?.label ?? '—',
    },
    {
      key: 'details', header: 'Details', type: 'strong', width: 'minmax(0,1.5fr)',
      value: (r) => (
        <>
          {r.title || r.category?.label || `Cat ${r.category_id}`}
          {r.recipient?.full_name && (
            <span className="font-normal text-[var(--color-muted)]">
              {' '}· {r.recipient.full_name.split(' ')[0]}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'amount', header: 'Amount', type: 'money', width: '120px',
      value: (r) => (r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'),
    },
    {
      key: 'status', header: 'Status', type: 'node', width: '120px',
      value: (r) => <StatusPill status={r.status} />,
    },
    {
      key: 'receipt', header: '', type: 'node', width: '160px',
      value: (r) => (r.status === 'confirmed' ? <ReceiptLink requestId={r.id} /> : null),
    },
  ];

  return (
    <PortalShell nav={nav} activeId="history" title="History" subtitle="Every payment across groceries, airtime and money.">
      <DataTable
        title="Closed orders"
        subtitle="Most recent first."
        summary={[
          { label: 'Delivered & received', value: String(confirmed.length) },
          { label: 'Closed orders', value: String(rows.length) },
          {
            label: 'Total received value',
            value: spent > 0 ? formatHtgMinor(spent) : '—',
            hint: spent > 0 ? `≈ ${formatUsdFromHtgMinor(spent)}` : undefined,
          },
        ]}
        filters={categories.length > 1 ? [{ key: 'all', label: 'All' }, ...categories] : undefined}
        activeFilter={filter}
        onFilter={setFilter}
        columns={columns}
        rows={visible}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        empty={
          <>
            No completed orders yet.{' '}
            <Link to="/customer/requests" className="font-semibold text-brand-600 hover:underline">
              See active requests →
            </Link>
          </>
        }
        renderCard={(r) => <HistoryCard r={r} />}
      />
    </PortalShell>
  );
}


function fmtDate(s?: string) {
  return s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

function HistoryCard({ r }: { r: ServiceRequestWithItems }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/customer/requests/${r.id}`} className="font-mono text-[12px] text-[#063B9E]">REQ-{String(r.id).padStart(4, '0')}</Link>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[13.5px] font-semibold text-[var(--color-ink)] truncate">{r.title || r.category?.label || `Cat ${r.category_id}`}</p>
      <div className="mt-1 flex items-center justify-between text-[12px] text-[var(--color-secondary-2)]">
        <span>For {r.recipient?.full_name?.split(' ')[0] ?? '—'} · {fmtDate(r.updated_at)}</span>
        <span className="font-mono text-[var(--color-ink)]">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'}</span>
      </div>
      {r.status === 'confirmed' && (
        <div className="mt-2 flex items-center gap-3">
          <Link to={`/customer/requests/new?reorder=${r.id}`} className="text-[11.5px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Order again</Link>
          <ReceiptLink requestId={r.id} />
        </div>
      )}
    </div>
  );
}


export function ReceiptLink({ requestId }: { requestId: number }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await receipts.download(requestId, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} onClick={(e) => e.stopPropagation()} className="text-[10px] border border-[var(--color-border)] rounded px-1 py-0.5 bg-white" aria-label="Receipt language">
        <option value="en">EN</option><option value="fr">FR</option><option value="ht">HT</option>
      </select>
      <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--color-success-fg)] hover:text-[var(--color-success-fg)]">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Receipt
      </button>
    </span>
  );
}
